import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewEncapsulation, HostListener, ViewChild } from '@angular/core';
import { UntypedFormControl} from '@angular/forms';
import { Subscription } from 'rxjs';

import * as uuid from "uuid";
import * as FileSaver from 'file-saver';
import { FileService } from '../../services/file.service';
import { ToastrService } from '../../services/toastr.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'mapfre-auditlib-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss']
})
export class FileUploaderComponent implements OnInit, OnDestroy {

  @Input()
  label

  @Input()
  placeholder

  @Input()
  entityType

  @Input()
  entityId

  @Input()
  allowedExtensions = ['PNG', 'JPEG', 'JPG', 'XLS', 'XLSX', 'DOC', 'DOCX', 'PDF', 'PPT', 'PPTX', 'ZIP', '7z', 'GZ', 'XLSM', 'XLSB', 'CSV', 'LOG', 'MSG', 'EML', 'TXT', 'RTF', 'HTML', 'HTM'];

  @Input()
  contentType = 'application/octet-stream';

  @Input()
  showComment = false;

  @Input()
  readonly = false;
  
  @Input()
  readonlyMessage = 'FILEUPLOADER.READONLY.DEFAULT';

  @Input()
  maxFiles: number | undefined;

  get effectiveMaxFiles(): number | undefined {
    if (!this.multiple) {
      return 1;
    }
    return this.maxFiles;
  }

  get isMaxFilesReached(): boolean {
    return this.effectiveMaxFiles != null && this.files.length >= this.effectiveMaxFiles;
  }

  get effectiveReadonly(): boolean {
    return this.readonly || this.isMaxFilesReached;
  }

  get effectiveReadonlyMessage(): string {
    if (this.isMaxFilesReached && !this.readonly) {
      return 'FILEUPLOADER.READONLY.MAX_FILES';
    }
    return this.readonlyMessage;
  }

  _form: UntypedFormControl;
  private _formSubscription: Subscription;

  @Input()
  get form() {
    return this._form
  }
  set form(value) {
    if (value) {
      this._formSubscription?.unsubscribe();
      this._form = value;
      this.formChange.emit(this._form);
      this.updateGroupedFiles();
      this._formSubscription = this._form.valueChanges.subscribe(() => this.updateGroupedFiles());
    }
  }
  @Output()
  formChange = new EventEmitter();

  @Output()
  onChange = new EventEmitter();

  @Input() icon;

  _model;
  @Input()
  get model() {
    return this._model
  }
  set model(value) {
    this._model = value;
    this.modelChange.emit(this._model);
  }

  @Output()
  modelChange = new EventEmitter();

  @Input()
  buttonIcon;
  @Output()
  buttonClicked = new EventEmitter();

  @Input()
  multiple = false;

  get files() {
    if (this.form?.value) {
      return this.multiple ? this.form.value : [this.form.value];
    } else {
      return [];
    }
  }

  get singleFile(): any {
    const file = this.files[0];
    return typeof file === 'string' ? { txtNam: file } : file;
  }

  groupedFiles: { label: string, files: { file: any, date: Date, icon: string }[] }[] = [];

  private readonly GROUP_KEYS = [
    { key: 'today', label: 'FILEUPLOADER.DATE_GROUPS.TODAY' },
    { key: 'thisWeek', label: 'FILEUPLOADER.DATE_GROUPS.THIS_WEEK' },
    { key: 'lastWeek', label: 'FILEUPLOADER.DATE_GROUPS.LAST_WEEK' },
    { key: 'older', label: 'FILEUPLOADER.DATE_GROUPS.OLDER' }
  ];

  private updateGroupedFiles(): void {
    const currentFiles = this.files;
    console.log('[FileUploader] updateGroupedFiles - files:', currentFiles);

    if (!currentFiles || currentFiles.length === 0) {
      this.groupedFiles = [];
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calcular el inicio de esta semana (lunes)
    const startOfThisWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfThisWeek.setDate(today.getDate() + diff);
    startOfThisWeek.setHours(0, 0, 0, 0);

    // Calcular el inicio de la semana pasada
    const startOfLastWeek = new Date(startOfThisWeek);
    startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);

    // Calcular el fin de la semana pasada (domingo)
    const endOfLastWeek = new Date(startOfThisWeek);
    endOfLastWeek.setDate(startOfThisWeek.getDate() - 1);

    const buckets: Record<string, { file: any, date: Date, icon: string }[]> = {
      today: [],
      thisWeek: [],
      lastWeek: [],
      older: []
    };

    currentFiles.forEach(file => {
      const normalizedFile = typeof file === 'string' ? { txtNam: file } : file;
      const fileDate = this.getFileDate(normalizedFile);
      const fileDateOnly = new Date(fileDate);
      fileDateOnly.setHours(0, 0, 0, 0);

      const entry = { file: normalizedFile, date: fileDate, icon: this.getIcon(normalizedFile?.name || normalizedFile?.txtNam) };

      if (fileDateOnly.getTime() === today.getTime()) {
        buckets.today.push(entry);
      } else if (fileDateOnly >= startOfThisWeek && fileDateOnly < today) {
        buckets.thisWeek.push(entry);
      } else if (fileDateOnly >= startOfLastWeek && fileDateOnly <= endOfLastWeek) {
        buckets.lastWeek.push(entry);
      } else {
        buckets.older.push(entry);
      }
    });

    // Ordenar cada grupo del más reciente al más antiguo
    const sortDesc = (a, b) => b.date.getTime() - a.date.getTime();
    Object.values(buckets).forEach(bucket => bucket.sort(sortDesc));

    // Construir array de grupos solo con los que tienen archivos
    this.groupedFiles = this.GROUP_KEYS
      .filter(g => buckets[g.key].length > 0)
      .map(g => ({ label: g.label, files: buckets[g.key] }));
  }

  getFileDate(file: any): Date {
    // API objects use camelCase date fields (dateInsert, dateUpdate, dateCreation)
    if (file?.uploadDate) {
      return new Date(file.uploadDate);
    }
    if (file?.dateInsert) {
      return new Date(file.dateInsert);
    }
    if (file?.dateUpdate) {
      return new Date(file.dateUpdate);
    }
    if (file?.dateCreation) {
      return new Date(file.dateCreation);
    }
    // Legacy / other field names kept as fallback
    if (file?.date) {
      return new Date(file.date);
    }
    if (file?.insDat) {
      return new Date(file.insDat);
    }
    if (file?.mdfDat) {
      return new Date(file.mdfDat);
    }
    if (file?.vrsDat) {
      return new Date(file.vrsDat);
    }
    // Newly added File (not yet saved): use last modified date
    if (file instanceof File) {
      return new Date(file.lastModified);
    }
    return new Date();
  }

  formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  @ViewChild("inputFile", { static: false })
  inputFileRef: any;

  @Output()
  downloadClicked = new EventEmitter();

  @Output()
  deleteClicked = new EventEmitter();

  isDragging = false;

  constructor(private fileService: FileService, private toastService: ToastrService, private translateService: TranslateService) { }

  ngOnInit() {
  }

  ngOnDestroy() {
    this._formSubscription?.unsubscribe();
  }

  @HostListener('keydown.enter', ['$event'])
  onButtonClicked() {
    this.buttonClicked.emit();
    this.form.markAsTouched();
  }

  onSelectFileClick() {
    this.form.markAsTouched();
    this.inputFileRef.nativeElement.click();
  }

  onDragOver(event: DragEvent) {
    if (this.effectiveReadonly) { return; }
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    if (this.effectiveReadonly) { return; }
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      this.processFiles(files);
    }
  }

  onInputChange(event) {
    if (event.target.files && event.target.files.length && event.target.files[0]) {
      this.processFiles(event.target.files);
    }
  }

  private processFiles(rawFiles: FileList) {
    const filesArray = Array.from(rawFiles);
    const file: File | File[] = this.multiple ? filesArray : filesArray[0];
    let mimeType = false;
    let repetido = false;
    if (this.form.value) {
      if (this.multiple) {
        for (const f of this.form.value) {
          for (const fil of (file as File[])) {
            if (fil.name === f) {
              repetido = true;
            }
          }
        }
      } else {
        for (const f of this.form.value) {
          if ((file as File).name === f) {
            repetido = true;
          }
        }
      }
    }
    //this.form.value VER SI TIENE EL MISMO NOMBRE QUE EL QUE SUBE Y SALIR DE AQUÍ
    if (!repetido) {
      if (this.multiple) {
        mimeType = !(file as File[]).every((item: File) => (new RegExp('(' + this.allowedExtensions.join('|').replace(/\./g, '\\.') + ')$')).test(item.name.toString().toUpperCase()));
      }
      else {
        mimeType = !(new RegExp('(' + this.allowedExtensions.join('|').replace(/\./g, '\\.') + ')$')).test((file as File).name.toString().toUpperCase());
      }
    }

    if (mimeType || repetido) {
      this.showMessage(repetido, mimeType);
    } else {
      this.form.markAllAsTouched();
      this.form.setValue(this.multiple ? [...(this.form.value ?? []), ...(file as File[])] : file);
      this.onChange.emit(file);
    }
  }

  showMessage(repetido: boolean, mimeType: boolean) {
    if (repetido) {
      this.toastService.add({
        severity: "warn",
        summary: this.translateService.instant('FILEUPLOADER.FILE_PAI.TITLE'),
        detail: this.translateService.instant('FILEUPLOADER.FILE_PAI.MESSAGE'),
        life: 5000
      });
    }
    if (mimeType) {
      this.toastService.add({
        severity: "warn",
        summary: this.translateService.instant('FILEUPLOADER.FILE_EXTENSION.TITLE'),
        detail: this.translateService.instant('FILEUPLOADER.FILE_EXTENSION.MESSAGE'),
        life: 5000
      });
    }
  }

  onDeleteClick(file) {
    const currentFiles: any[] = this.form.value || [];

    // Helper: extract the unique S3 key (or native File reference) for comparison
    const getKey = (f: any): any => {
      if (f instanceof File) { return f; }          // native File → identity
      if (typeof f === 'string') { return f; }       // string S3 key
      return f?.txtNam || f?.name || f;             // API object → S3 key field
    };

    const targetKey = getKey(file);

    // Primary: reference equality; fallback: key-based equality
    let idx = currentFiles.indexOf(file);
    if (idx === -1) {
      idx = currentFiles.findIndex(f => {
        if (f === file) { return true; }
        return getKey(f) === targetKey;
      });
    }

    console.log('[FileUploader] onDeleteClick – file:', file, '| idx:', idx, '| total:', currentFiles.length);

    if (idx === -1) { return; }

    const files = [...currentFiles];
    files.splice(idx, 1);
    this.form.setValue(this.multiple && files.length ? files : null);
    this.form.markAllAsTouched();
    this.inputFileRef.nativeElement.value = ''; //065335 Parte 2 solucionada.
  }

  onDownloadClick(file) {
    let key: string;
    let filename: string;

    if (typeof file === 'string') {
      // Existing file loaded from API: the form value IS the S3 key (e.g. "uuid/filename.pdf")
      key = file;
      filename = file.includes('/') ? file.split('/').pop() : file;
    } else if (file instanceof File) {
      // Browser File object (not yet saved) — should not be downloadable, but kept as safety fallback
      filename = file.name;
      key = `${uuid.v4()}/${filename}`;
    } else {
      // API file object: txtNam (or name) holds the full S3 key (e.g. "uuid/filename.pdf")
      key = file?.txtNam || file?.name;
      filename = key?.includes('/') ? key.split('/').pop() : key;
    }

    const effectiveContentType = this.contentType || 'application/octet-stream';

    if (this.entityType === 'dealComittee' || this.entityType === 'dealComitteeEntrace') {
      const str = file;
      const splitted = str.split(/_(.+)/, 2); //identificamos _Nombre y lo separamos en dos
      const idDocument = parseInt(splitted[0], 10);
      const filename2 = splitted[1];
      const key2 = `${uuid.v4()}/${filename2}`;
      this.fileService.downloadSpecial(key2, idDocument, this.entityType, this.entityId, effectiveContentType).subscribe((res: any) => {
        const f = new File([res], filename2, { type: effectiveContentType });
        FileSaver.saveAs(f);
      })
    } else {
      this.fileService.download(key, this.entityType, this.entityId, effectiveContentType).subscribe((res: any) => {
        const f = new File([res], filename, { type: effectiveContentType });
        FileSaver.saveAs(f);
      })
    }
  }


  isNewFile(file: any): boolean {
    return file instanceof File;
  }

  getIcon(name: string): string {
    const lower = (name || '').toLowerCase();

    if (lower.includes('xls')) {
      return 'icon-24-px-doc-excel green-excel';
    }

    if (lower.includes('pdf')) {
      return 'icon-24-px-doc-pdf red-pdf';
    }

    if (lower.includes('ppt')) {
      return 'icon-24-px-doc-ppt orange-ppt';
    }

    if (lower.includes('doc')) {
      return 'icon-24-px-doc-word blue-doc';
    }

    return 'icon-24-px-comunicacion';
  }


}
